import DeleteIcon from '@src/svgs/DeleteIcon';
import MicrophoneIcon from '@src/svgs/MicrophoneIcon';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

interface SpeakerContextMenuProps {
  isOpen: boolean;
  buttonRef: React.RefObject<HTMLElement> | null; // Reference to the button that triggered the menu
  onClose: () => void;
  onEdit: () => void;
  onAddToStory?: () => void; // Callback for adding speaker to story
  onRemoveFromStory?: () => void; // Callback for removing speaker from story
  onUnassign?: () => void; // Callback for unassigning speaker (removing from voice mappings)
  speakerName: string;
  isAnonymous?: boolean; // Flag to indicate if this is an anonymous speaker
  showAddToStory?: boolean; // Flag to show "Add to Story" option
  showRemoveFromStory?: boolean; // Flag to show "Remove from Story" option
  showUnassign?: boolean; // Flag to show "Unassign Speaker" option
}

/**
 * Context menu for speaker actions that appears when clicking the overflow button
 */
const SpeakerContextMenu: React.FC<SpeakerContextMenuProps> = ({
  isOpen,
  buttonRef,
  onClose,
  onEdit,
  onAddToStory,
  onRemoveFromStory,
  onUnassign,
  speakerName,
  isAnonymous = false,
  showAddToStory = false,
  showRemoveFromStory = false,
  showUnassign = false,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Handle escape key press
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Calculate position relative to button
  useEffect(() => {
    if (!isOpen || !menuRef.current || !buttonRef?.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuElement = menuRef.current;

    // Position menu to the left of the button
    menuElement.style.position = 'fixed';
    menuElement.style.top = `${buttonRect.top}px`;
    menuElement.style.left = `${
      buttonRect.left - menuElement.offsetWidth - 5
    }px`;
  }, [isOpen, buttonRef]);

  if (!isOpen) return null;

  return (
    <MenuContainer ref={menuRef} data-speaker-menu={true}>
      <MenuItem onClick={onEdit}>
        <MenuIcon>
          <MicrophoneIcon width="14" height="14" />
        </MenuIcon>
        {isAnonymous ? 'Assign Speaker' : 'Edit Speaker'}
      </MenuItem>
      {showAddToStory && onAddToStory && !isAnonymous && (
        <MenuItem
          onClick={() => {
            onAddToStory();
            onClose();
          }}
        >
          <MenuIcon>📝</MenuIcon>
          Add to Story
        </MenuItem>
      )}
      {showRemoveFromStory && onRemoveFromStory && !isAnonymous && (
        <MenuItem
          onClick={() => {
            onRemoveFromStory();
            onClose();
          }}
        >
          <MenuIcon>
            <DeleteIcon width="13" height="13" strokeColor="#F3E9D7" />
          </MenuIcon>
          Remove from Story
        </MenuItem>
      )}
      {showUnassign && onUnassign && !isAnonymous && (
        <MenuItem
          onClick={() => {
            onUnassign();
            onClose();
          }}
        >
          <MenuIcon>
            <DeleteIcon width="13" height="13" strokeColor="#F3E9D7" />
          </MenuIcon>
          Unassign Speaker
        </MenuItem>
      )}
    </MenuContainer>
  );
};

const MenuContainer = styled.div`
  position: fixed; /* Will be positioned programmatically in useEffect */
  min-width: 140px;
  padding: 0 8px;
  background-color: #03041a;
  border-radius: 4px;
  border: 1px solid #484848;
  box-shadow: 8px 16px 8px 0 rgba(0, 0, 0, 0.4);
  overflow: hidden;
  z-index: 1000;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 0;
  border: none;
  color: #f3e9d7;
  font-size: 12px;
  background: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:not(:last-child) {
    border-bottom: 1px solid #484848;
  }

  &:hover,
  &:active {
    color: #f2d093;
  }
`;

const MenuIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default SpeakerContextMenu;
